import type { Theme } from '@goodtime/engine';
import { buildSlotAssets } from './slotAssets';

const ASSETS = buildSlotAssets('gardens', {
  cherry:  'daisy',
  lemon:   'tulip',
  orange:  'hummingbird',
  bell:    'rosebush',
  star:    'butterfly_wild',
  seven:   'topiary',
  gem:     'fountain',
  scatter: 'peacock_scatter',
  money:   'royal_bouquet',
});

/** Castle Gardens — the Kingdom narrative arc's polite interlude.
 *  Sits between Royal Court and Knights' Quest on the Kingdom map:
 *  after the player has met the court but before the joust kicks
 *  off, the gardens are a tonal palate-cleanser. Rose-pink and
 *  leaf-green palette, manicured-Versailles iconography.
 *
 *  <p><b>Mechanical personality — "Gentle Bloom":</b>
 *  <ul>
 *    <li>The most generous reel weight in the deck: commons (daisy,
 *        tulip) at 6 and the money symbol (royal bouquet) at 2.5 —
 *        higher than any other theme. Small wins are nearly constant.
 *    <li>Wheel has no zero segments at all and caps at x5. Top end
 *        is deliberately low — Castle Gardens isn't where you hit a
 *        jackpot, it's where you stop bleeding chips after Dragon's
 *        Hoard runs you dry.
 *    <li>Scatter is rare (peacock at 1) but bonus rounds, when they
 *        land, are also gentle — Castle Gardens doesn't swing for
 *        big-money moments, it's the recovery zone.
 *  </ul>
 *  Intended feel: every spin blooms. The gardens never disappoint;
 *  they also never thrill. */
export const GARDENS_THEME: Theme = {
  id: 'gardens',
  name: 'Castle Gardens',
  palette: {
    accent: 0xe48fa0,         // rose pink
    secondary: 0x7dbc77,      // leaf green
    warm: 0xfff2a8,           // cream butter
    frameFill: 0x5e4a4f,      // dusty mauve
    bgGradientTop: 0xdfe9f5,  // pale sky
    bgGradientBottom: 0xc0a8c5, // lavender dusk
    dimOverlay: 0x05080d,
  },
  slot: {
    symbols: [
      // Common-heavy curve so the reels rarely come up empty —
      // daisy/tulip/hummingbird carry the spin-to-spin pace and money
      // (royal bouquet) at 2.5 keeps the jackpot symbol reachable.
      // Premiums (topiary/fountain) stay modest at 2 each — Gardens
      // is about frequency, not magnitude.
      { id: 'cherry',  label: 'Daisy',        glyph: '🌼', tint: 0xfff2a8, weight: 6, textureAlias: 'gardens:cherry',
        winVideoAlias: 'gardens:cherry:win:video', winVideoSpeed: 2 },
      { id: 'lemon',   label: 'Tulip',        glyph: '🌷', tint: 0xe48fa0, weight: 6, textureAlias: 'gardens:lemon',
        winVideoAlias: 'gardens:lemon:win:video', winVideoSpeed: 2 },
      { id: 'orange',  label: 'Hummingbird',  glyph: '🐦', tint: 0x7dbc77, weight: 4, textureAlias: 'gardens:orange',
        winVideoAlias: 'gardens:orange:win:video', winVideoSpeed: 2 },
      { id: 'bell',    label: 'Rosebush',     glyph: '🌹', tint: 0xc73e3e, weight: 3, textureAlias: 'gardens:bell',
        winVideoAlias: 'gardens:bell:win:video', winVideoSpeed: 2 },
      { id: 'star',    label: 'Butterfly',    glyph: '🦋', tint: 0xe8b83a, weight: 2, textureAlias: 'gardens:star', // wild
        winVideoAlias: 'gardens:star:win:video', winVideoSpeed: 2 },
      { id: 'seven',   label: 'Topiary',      glyph: '🌳', tint: 0x7dbc77, weight: 2, textureAlias: 'gardens:seven',
        winVideoAlias: 'gardens:seven:win:video', winVideoSpeed: 2 },
      { id: 'gem',     label: 'Fountain',     glyph: '⛲', tint: 0xc0a8c5, weight: 2, textureAlias: 'gardens:gem',
        winVideoAlias: 'gardens:gem:win:video', winVideoSpeed: 2 },
      { id: 'scatter', label: 'Peacock',      glyph: '🦚', tint: 0x3e5baf, weight: 1, textureAlias: 'gardens:scatter',
        winVideoAlias: 'gardens:scatter:win:video', winVideoSpeed: 2 },
      { id: 'money',   label: 'Royal Bouquet',glyph: '💐', tint: 0xe48fa0, weight: 2.5,
        textureAlias: 'gardens:money',
        winVideoAlias: 'gardens:money:win:video', winVideoSpeed: 2,
        isMoney: true },
      // Gem-on-reel — producer brief 2026-06-04. Scatter-style bonus
      // shared across every biome (registered via SHARED_REEL_SYMBOLS
      // in themes/index.ts). Weights deliberately rare so a gem
      // landing feels like a treat rather than background noise.
      { id: 'gem_t5',  label: 'Gems',     glyph: '💎', tint: 0x3ea7e8, weight: 0.10, gemValue: 5,  textureAlias: 'shared:reel:gem_5' },
      { id: 'gem_t10', label: 'Gem Pile', glyph: '💎', tint: 0x3ea7e8, weight: 0.05, gemValue: 10, textureAlias: 'shared:reel:gem_10' },
    ],
    wildSymbolId: 'star',
    scatterSymbolId: 'scatter',
  },
  wheel: {
    // Soft-cap wheel: no zero segments at all, no jackpot, top caps
    // at x5. The x1/x2/x3 cluster dominates so the wheel basically
    // always pays out something — and never anything dramatic. This
    // is the recovery zone after Dragon's bust runs.
    segments: [
      { id: 'x1-a',   kind: 'multiplier', label: '×1',  tint: 0xfff2a8, multiplier: 1,  weight: 5 },
      { id: 'x2-a',   kind: 'multiplier', label: '×2',  tint: 0xe48fa0, multiplier: 2,  weight: 5 },
      { id: 'x3-a',   kind: 'multiplier', label: '×3',  tint: 0x7dbc77, multiplier: 3,  weight: 4 },
      { id: 'x1-b',   kind: 'multiplier', label: '×1',  tint: 0xfff2a8, multiplier: 1,  weight: 4 },
      { id: 'x2-b',   kind: 'multiplier', label: '×2',  tint: 0xe48fa0, multiplier: 2,  weight: 5 },
      { id: 'x3-b',   kind: 'multiplier', label: '×3',  tint: 0x7dbc77, multiplier: 3,  weight: 4 },
      { id: 'x1-c',   kind: 'multiplier', label: '×1',  tint: 0xfff2a8, multiplier: 1,  weight: 4 },
      { id: 'x5-a',   kind: 'multiplier', label: '×5',  tint: 0xc0a8c5, multiplier: 5,  weight: 2 },
      { id: 'x2-c',   kind: 'multiplier', label: '×2',  tint: 0xe48fa0, multiplier: 2,  weight: 5 },
      { id: 'x3-c',   kind: 'multiplier', label: '×3',  tint: 0x7dbc77, multiplier: 3,  weight: 4 },
      { id: 'x1-d',   kind: 'multiplier', label: '×1',  tint: 0xfff2a8, multiplier: 1,  weight: 4 },
      { id: 'x5-b',   kind: 'multiplier', label: '×5',  tint: 0xc0a8c5, multiplier: 5,  weight: 2 },
    ],
  },
  assets: {
    slot: ASSETS.slot,
    slotWin: ASSETS.slotWin,
    scene: {
      background:       { alias: 'gardens:bg',         path: 'environment/gardens.png' },
      columnBackground: { alias: 'gardens:column_bg',  path: 'reel/gardens_column_bg.png' },
      border:           { alias: 'gardens:border',     path: 'brand/border_forest.png' },
    },
  },
};
